import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page, usePoll, Empty, Chip, Pill, matchName, seeded } from './ui'
import Scorecard from './Scorecard'

/**
 * Order of play for one sport at one venue — the board by the door.
 *
 * Every court gets a column: what is on it now, and the two or three matches
 * queued behind. Twelve carrom boards in a room need this; a single badminton
 * court does not, and gets a screen of its own instead.
 */
const VenueBoard = () => {
  const { city, sport } = useParams()
  const [params, setParams] = useSearchParams()
  const date = params.get('date') || undefined
  const [openMatch, setOpenMatch] = useState(null)

  const { data, error } = usePoll(
    () => reportingApi.venue({ city, sport, date }),
    15000,
    [city, sport, date],
  )

  if (error) return <div style={page}><Empty>{error}</Empty></div>
  if (!data) return <div style={page}><Empty>Loading order of play…</Empty></div>

  const liveCount = data.courts.filter((c) => c.now).length

  return (
    <div style={page}>
      <Link to={`/board/city/${encodeURIComponent(city)}`} style={back}>← {city}</Link>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        gap: 16, flexWrap: 'wrap', margin: '8px 0 18px',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26 }}>{data.label} · {city}</h2>
          <p style={{ margin: '4px 0 0', color: T.muted, fontSize: 13.5 }}>
            {[data.venue, data.day_label].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div style={{ fontSize: 13, color: T.muted }}>
          {liveCount} of {data.courts.length} {data.unit_noun.toLowerCase()}s in play
        </div>
      </div>

      {data.days.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {data.days.map((d) => (
            <Chip key={d.date} on={d.date === data.date}
                  onClick={() => setParams(d.date === data.date ? {} : { date: d.date })}>
              {d.label}{d.live > 0 ? ` · ${d.live} live` : ''}
            </Chip>
          ))}
        </div>
      )}

      <div style={{
        display: 'grid', gap: 12,
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      }}>
        {data.courts.map((c) => (
          <article key={c.court} style={{
            background: T.card, borderRadius: 12, overflow: 'hidden',
            border: `1px solid ${c.now ? '#f0cdb4' : T.line}`,
          }}>
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '11px 16px', borderBottom: `1px solid ${T.line}`,
              background: c.now ? T.liveBg : '#fbfcfd',
            }}>
              <strong style={{ fontSize: 15 }}>{c.court}</strong>
              <Link to={`/board/screen/${encodeURIComponent(city)}/${sport}/${encodeURIComponent(c.court)}`}
                    style={{
                      fontSize: 11.5, fontWeight: 700, color: T.accent, textDecoration: 'none',
                      letterSpacing: '.04em', textTransform: 'uppercase',
                    }}>Screen →</Link>
            </header>

            <div style={{ padding: '14px 16px' }}>
              {c.now ? (
                <button onClick={() => setOpenMatch(c.now.id)} style={nowBtn}>
                  <Pill status="live" />
                  <div style={{ marginTop: 8, fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>
                    {seeded(c.now.home, c.now.home_seed)}<br />
                    <span style={{ color: T.faint, fontWeight: 500, fontSize: 12.5 }}>v</span><br />
                    {seeded(c.now.away, c.now.away_seed)}
                  </div>
                  {c.now.summary && (
                    <div style={{
                      marginTop: 8, fontSize: 24, fontWeight: 800, color: T.live,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{c.now.summary}</div>
                  )}
                  {c.now.note && (
                    <div style={{ marginTop: 3, fontSize: 12, color: T.muted }}>{c.now.note}</div>
                  )}
                </button>
              ) : (
                <p style={{ margin: 0, color: T.faint, fontSize: 13 }}>Free</p>
              )}

              {c.next.length > 0 && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${T.line}`, paddingTop: 10 }}>
                  <div style={{
                    fontSize: 10.5, letterSpacing: '.09em', textTransform: 'uppercase',
                    color: T.faint, fontWeight: 700, marginBottom: 6,
                  }}>Next</div>

                  {c.next.map((n) => (
                    <div key={n.id} style={{
                      display: 'flex', gap: 10, fontSize: 12.5, color: T.muted, padding: '3px 0',
                    }}>
                      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: T.ink }}>
                        {n.start_time || '—'}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {matchName(n)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 12, fontSize: 11.5, color: T.faint }}>
                {c.done} of {c.total} played on this {data.unit_noun.toLowerCase()}
              </div>
            </div>
          </article>
        ))}
      </div>

      {data.courts.length === 0 && <Empty>Nothing scheduled here on this day.</Empty>}

      {openMatch && <Scorecard fixtureId={openMatch} onClose={() => setOpenMatch(null)} />}
    </div>
  )
}

const nowBtn = {
  width: '100%', textAlign: 'left', background: 'transparent', border: 0,
  padding: 0, cursor: 'pointer', font: 'inherit', fontFamily: 'inherit', color: T.ink,
}
const back = { fontSize: 13, color: T.muted, textDecoration: 'none', fontWeight: 600 }

export default VenueBoard
